/* Carrying the picture somebody is working on from one tool to the next.
 *
 * THE PROBLEM. Every tool here is its own document: /watermark-image and
 * /flip-image are separate HTML pages that each boot the same application, and
 * that is deliberate -- it is what gives each one a URL a search engine can
 * index. The cost is that navigating between them is a full page load, so the
 * Pinia store goes with it. Somebody who shared a photograph into the app from
 * their phone, then used the All tools menu to reach the tool they actually
 * wanted, arrived at an empty drop zone with their photograph gone. Reported
 * from an Android share, which is the worst version of it: the file came from
 * another app, so "pick it again" means going back out to the gallery.
 *
 * WHY A CACHE AND A SESSION KEY, and not one or the other.
 *
 * Cache Storage holds the bytes because it is the one client-side store that
 * takes a Blob as it is, at photograph size, without base64 in the middle.
 * service-worker.js already reaches the same conclusion for the share target,
 * and for a related reason -- see its header, which explains why a postMessage
 * across a navigation is a race that loses.
 *
 * sessionStorage holds the POINTER to it, and that is what scopes the whole
 * thing correctly. sessionStorage survives a same-tab navigation and dies with
 * the tab, which is exactly the shape of "while I am moving around this site":
 *
 *     switch tool in the same tab   ->  the pointer is there, picture carries
 *     open the site in a new tab    ->  no pointer, nothing carries
 *     come back tomorrow            ->  no pointer, nothing carries
 *
 * That last column is the point. A photograph reappearing in a fresh tab, or on
 * a shared laptop the next morning, would be a nasty surprise however
 * convenient it was the first time. The TTL below is only a backstop for a
 * long-lived tab; the tab lifetime is the real rule.
 *
 * NOTHING HERE GOES ANYWHERE. This is the visitor's own file, written to their
 * own browser's storage on their own device, and read back by the same site a
 * moment later. It is not uploaded, and restoring it deliberately does NOT
 * start an upload -- see the caller.
 */

const CARRY_CACHE = "imgopt-carry-v1"
const CARRY_KEY = "imgopt-carry"
const CARRY_TAB_KEY = "imgopt-carry-tab"
const CARRY_PREFIX = "/_carried/"

/* A backstop, not the rule. The tab is the rule. This only matters for a tab
 * left open for hours with a photograph in it, where quietly forgetting is
 * better than quietly remembering. */
const CARRY_MAX_AGE_MS = 30 * 60 * 1000

/* Big enough for any phone photograph, small enough that a mistake cannot fill
 * somebody's disk. The intake cap is 50 MB, so this refuses nothing a visitor
 * can actually stage. */
const CARRY_MAX_BYTES = 60 * 1024 * 1024

// Every one of these is wrapped, and every failure is silent. Storage can be
// absent, full, or refused outright -- Safari in private browsing used to
// throw on merely touching sessionStorage -- and none of that is a reason to
// interrupt somebody who is editing a photograph. Losing the carry-over costs
// them a re-pick, which is the behaviour we had before any of this existed.
function pointer() {
	try {
		const raw = window.sessionStorage.getItem(CARRY_KEY)
		return raw ? JSON.parse(raw) : null
	} catch (e) {
		return null
	}
}

function setPointer(value) {
	try {
		if (value) window.sessionStorage.setItem(CARRY_KEY, JSON.stringify(value))
		else window.sessionStorage.removeItem(CARRY_KEY)
	} catch (e) {
		/* nothing carries; the drop zone still works */
	}
}

function usable() {
	return typeof window !== "undefined" && "caches" in window
}

/* An id for THIS tab, minted once and kept in sessionStorage beside the
 * pointer, so it survives navigating between tools and is different in every
 * other tab.
 *
 * The cache is shared by the whole origin while sessionStorage is not, and
 * without this the two disagree in a way that only shows up with two tabs
 * open: emptying the cache before writing -- which one entry at a time
 * requires -- threw away the OTHER tab's picture, leaving its pointer aimed at
 * nothing. Scoping the key by tab means each one keeps its own, and sweep()
 * below is what stops a closed tab's bytes lying around for ever.
 */
function tabId() {
	try {
		let id = window.sessionStorage.getItem(CARRY_TAB_KEY)
		if (!id) {
			id = Math.random().toString(36).slice(2) + Date.now().toString(36)
			window.sessionStorage.setItem(CARRY_TAB_KEY, id)
		}
		return id
	} catch (e) {
		return ""
	}
}

// The timestamp is in the key, so age can be judged without reading a body.
function stampOf(url) {
	const m = new RegExp(CARRY_PREFIX + "[^/]+/(\\d+)/").exec(url)
	return m ? Number(m[1]) : 0
}

/* Clear anything left behind, whoever left it.
 *
 * Called on boot. sessionStorage dies with its tab and the cache does not, so a
 * tab closed with a picture in it leaves bytes that nothing else would ever
 * come back for -- there is no pointer left to notice them. Judging by AGE
 * rather than by "has no pointer in this tab" is the part that matters: every
 * pointer is invisible from every other tab, so the second test would have had
 * one tab confidently deleting another's picture.
 */
export async function sweep() {
	if (!usable()) return
	try {
		const cache = await caches.open(CARRY_CACHE)
		const now = Date.now()
		for (const key of await cache.keys()) {
			const at = stampOf(key.url)
			if (!at || now - at > CARRY_MAX_AGE_MS) await cache.delete(key)
		}
	} catch (e) {
		/* storage unavailable; nothing to sweep */
	}
}

/* Put one file aside.
 *
 * ONE, not a batch. Twenty phone photographs is a couple of hundred megabytes
 * of somebody's disk for a convenience, and switching tools mid-batch is not
 * the journey this exists for -- the reported one was a single shared photo.
 * A batch simply does not carry, and clears whatever was carried before it so
 * that going single -> batch -> single cannot resurrect the first picture.
 */
export async function keep(file) {
	if (!usable()) return
	if (!file || !file.size || file.size > CARRY_MAX_BYTES) {
		await drop()
		return
	}
	const at = Date.now()
	// Inside the guard, not above it: a throw here would otherwise be an
	// unhandled rejection with no fallback, and nothing would carry with no
	// sign of why.
	let url
	const tab = tabId()
	try {
		url = new URL(CARRY_PREFIX + tab + "/" + at + "/"
			+ encodeURIComponent(file.name || "picture"), window.location.href).href
		const cache = await caches.open(CARRY_CACHE)
		// One at a time PER TAB. Anything else here belongs to another tab that
		// is still using it, or is old enough for sweep() to take.
		for (const stale of await cache.keys()) {
			if (stale.url.indexOf(CARRY_PREFIX + tab + "/") !== -1) await cache.delete(stale)
		}
		await cache.put(url, new Response(file, {
			headers: {"content-type": file.type || "application/octet-stream"},
		}))
	} catch (e) {
		await drop()
		return
	}
	setPointer({url, name: file.name || "picture", type: file.type || "", at})
}

/* Take it back, and LEAVE IT WHERE IT IS.
 *
 * Unlike the share-target hand-off, which deletes on read because a share
 * happens once, this survives being collected: somebody may pass through three
 * tools looking for the right one, and the picture has to still be there on the
 * third. The timestamp is refreshed on the way through so that the backstop
 * measures idleness rather than the age of the original choice.
 *
 * `accepts` is the caller's rule about what THIS page will take -- a PDF
 * carried to a page that only takes images must not be staged, and must not be
 * thrown away either, because the next tool along may want it. Refusing quietly
 * is the whole behaviour: an alert on page load about a file nobody just chose
 * would be worse than the bug this fixes.
 */
export async function take(accepts) {
	if (!usable()) return null
	const ptr = pointer()
	if (!ptr || !ptr.url) return null
	if (!ptr.at || Date.now() - ptr.at > CARRY_MAX_AGE_MS) {
		await drop()
		return null
	}
	let file = null
	try {
		const cache = await caches.open(CARRY_CACHE)
		const hit = await cache.match(ptr.url)
		if (!hit) {
			// The pointer outlived the bytes: storage was cleared under us.
			await drop()
			return null
		}
		const blob = await hit.blob()
		file = new File([blob], ptr.name, {type: ptr.type || blob.type || ""})
	} catch (e) {
		return null
	}
	if (typeof accepts === "function" && !accepts(file)) return null
	setPointer({...ptr, at: Date.now()})
	return file
}

/* Forget it. Called when the visitor takes the picture off the stage, or resets
 * everything, or hands over a finished file -- at which point keeping their
 * photograph in storage buys nothing and only widens the window in which it
 * could turn up somewhere they did not expect. */
export async function drop() {
	const tab = tabId()
	setPointer(null)
	if (!usable()) return
	try {
		const cache = await caches.open(CARRY_CACHE)
		for (const key of await cache.keys()) {
			// This tab's own, and nobody else's.
			if (!tab || key.url.indexOf(CARRY_PREFIX + tab + "/") !== -1) {
				await cache.delete(key)
			}
		}
	} catch (e) {
		/* already gone, or storage unavailable */
	}
}

export { CARRY_CACHE, CARRY_KEY, CARRY_TAB_KEY, CARRY_MAX_AGE_MS, CARRY_MAX_BYTES }
